import React from 'react';
import { clsx } from 'clsx';
import {
  BuildingOffice2Icon,
  CheckBadgeIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';

export interface PlatformKpiStats {
  funerariasTotal: number;
  funerariasActivas: number;
  usuariosEnFunerarias: number;
}

interface KpiItemProps {
  label: string;
  value: number;
  icon: React.ComponentType<{ className?: string }>;
  accent: 'slate' | 'emerald' | 'navy';
  footnote?: string;
}

const accentMap = {
  slate: {
    ring: 'ring-slate-200/80',
    iconBg: 'bg-slate-100 text-slate-700',
    value: 'text-slate-900',
  },
  emerald: {
    ring: 'ring-emerald-200/80',
    iconBg: 'bg-emerald-50 text-emerald-700',
    value: 'text-emerald-800',
  },
  navy: {
    ring: 'ring-slate-300/60',
    iconBg: 'bg-[#0f172a] text-white',
    value: 'text-slate-900',
  },
};

const KpiItem: React.FC<KpiItemProps> = ({ label, value, icon: Icon, accent, footnote }) => {
  const a = accentMap[accent];
  return (
    <div
      className={clsx(
        'group relative overflow-hidden rounded-2xl border border-slate-200/90 bg-white p-5 shadow-sm',
        'transition-all duration-200 hover:border-slate-300 hover:shadow-md'
      )}
    >
      <div className={clsx('absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent')} />
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</p>
          <p className={clsx('mt-2 text-3xl font-bold tabular-nums tracking-tight', a.value)}>{value}</p>
          {footnote && <p className="mt-1.5 text-xs text-slate-400">{footnote}</p>}
        </div>
        <div
          className={clsx(
            'flex h-12 w-12 shrink-0 items-center justify-center rounded-xl shadow-inner ring-1',
            a.iconBg,
            a.ring
          )}
        >
          <Icon className="h-6 w-6" aria-hidden />
        </div>
      </div>
    </div>
  );
};

export const PlatformKpiCards: React.FC<{ stats: PlatformKpiStats; isLoading?: boolean }> = ({
  stats,
  isLoading,
}) => {
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className="h-[124px] animate-pulse rounded-2xl border border-slate-200 bg-slate-100/80"
          />
        ))}
      </div>
    );
  }

  const inactive = Math.max(0, stats.funerariasTotal - stats.funerariasActivas);
  const pctActive =
    stats.funerariasTotal > 0
      ? Math.round((stats.funerariasActivas / stats.funerariasTotal) * 100)
      : null;

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
      <KpiItem
        label="Funerarias registradas"
        value={stats.funerariasTotal}
        icon={BuildingOffice2Icon}
        accent="slate"
        footnote={
          stats.funerariasTotal === 0
            ? 'Añade tu primer cliente'
            : inactive > 0
              ? `${inactive} suspendida(s)`
              : 'Todas con servicio activo'
        }
      />
      <KpiItem
        label="Activas"
        value={stats.funerariasActivas}
        icon={CheckBadgeIcon}
        accent="emerald"
        footnote={pctActive !== null ? `${pctActive}% del total` : undefined}
      />
      <KpiItem
        label="Usuarios en funerarias"
        value={stats.usuariosEnFunerarias}
        icon={UsersIcon}
        accent="navy"
        footnote="Cuentas en todos los espacios"
      />
    </div>
  );
};
